import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../../mock/mock";
import { trackEvent } from "../../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ApplyForm() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | submitting | success

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setStatus("submitting");
    trackEvent("cta_click", { label: "form_submit", location: "apply_form" });
    try {
      // Attempt real submit; if backend not yet wired, we still show success and redirect.
      await axios.post(`${API}/applications`, form, { timeout: 15000 });
      trackEvent("application_submitted", { ok: true });
    } catch (err) {
      // Non-blocking: we still proceed to the booking step (Michelle can follow up manually)
      console.warn("Application submit failed, proceeding to booking.", err?.message);
      trackEvent("application_submitted", { ok: false, error: err?.message || "unknown" });
    }
    setStatus("success");
    toast.success("Application received. Redirecting you to book a call…");
    setTimeout(() => {
      trackEvent("redirect_to_tidycal", { url: SITE.tidyCalUrl });
      window.location.href = SITE.tidyCalUrl;
    }, 1800);
  };

  return (
    <section id="apply" className="bg-[#fdfaf5]">
      <div className="max-w-5xl mx-auto px-4 lg:px-8 py-16 md:py-24">
        <div className="grid md:grid-cols-2 gap-10 md:gap-14 items-start">
          <div>
            <p className="eyebrow text-lg">Apply</p>
            <div className="mt-3 ornament-line" />
            <h2 className="mt-4 font-serif-display text-3xl md:text-5xl leading-tight text-[#1a1a1a]">
              Ready to build your talk and take the stage
            </h2>
            <p className="mt-4 text-[15px] md:text-base text-[#1a1a1a]/75 leading-relaxed">
              Once you apply, you’ll be redirected to book a short call so we can explore fit and answer any questions before you commit.
            </p>

            <ul className="mt-6 space-y-2 text-[14.5px] text-[#1a1a1a]/70">
              <li>• 6-person cohort for the October 2026 intake</li>
              <li>• 8-week program with a 2-day Sydney intensive and live showcase</li>
              <li>• Investment: $2,997 AUD</li>
            </ul>
          </div>

          <form onSubmit={onSubmit} className="bg-white border border-[#e2d5bb] rounded-2xl p-6 md:p-8 shadow-[0_30px_60px_-40px_rgba(0,0,0,0.25)]">
            {status === "success" ? (
              <div className="py-8 text-center">
                <div className="mx-auto h-14 w-14 rounded-full bg-[#7a1e2f]/10 text-[#7a1e2f] grid place-items-center">
                  <CheckCircle2 size={28} />
                </div>
                <h3 className="mt-4 font-serif-display text-2xl text-[#1a1a1a]">Application received</h3>
                <p className="mt-2 text-[#1a1a1a]/70">Redirecting you to book a call with Michelle…</p>
                <div className="mt-4 inline-flex items-center gap-2 text-[#7a1e2f]"><Loader2 className="animate-spin" size={16} /> One moment</div>
              </div>
            ) : (
              <>
                <Field label="Full Name" name="full_name" value={form.full_name} onChange={onChange} placeholder="Your full name" required />
                <Field label="Email Address" name="email" type="email" value={form.email} onChange={onChange} placeholder="you@example.com" required />
                <Field label="Phone Number" name="phone" value={form.phone} onChange={onChange} placeholder="e.g. +61 4…" />
                <div className="mt-4">
                  <label className="block text-[12px] tracking-[0.18em] uppercase text-[#1a1a1a]/60 mb-2">What&apos;s the talk you want to build, and why now?</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    placeholder="Tell Michelle a little about your talk and timing…"
                    className="w-full rounded-xl border border-[#dccbb1] bg-[#fdfaf5] px-4 py-3 text-[15px] focus:border-[#7a1e2f] focus:ring-2 focus:ring-[#7a1e2f]/20 transition-colors"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-6 w-full btn-primary rounded-full px-6 py-3.5 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-2 disabled:opacity-70"
                >
                  {status === "submitting" ? (<><Loader2 className="animate-spin" size={16} /> Submitting…</>) : (<>Apply Now <ArrowRight size={16} /></>)}
                </button>

                <p className="mt-4 text-center text-[12.5px] text-[#1a1a1a]/55">
                  After you submit your application, you’ll be redirected to book a short call so we can explore fit and answer any questions before you commit.
                </p>
              </>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = "text", placeholder, required }) {
  return (
    <div className="mt-4 first:mt-0">
      <label className="block text-[12px] tracking-[0.18em] uppercase text-[#1a1a1a]/60 mb-2">{label}{required && <span className="text-[#8b1b31] ml-1">*</span>}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        required={required}
        className="w-full rounded-xl border border-[#dccbb1] bg-[#fdfaf5] px-4 py-3 text-[15px] focus:border-[#7a1e2f] focus:ring-2 focus:ring-[#7a1e2f]/20 transition-colors"
      />
    </div>
  );
}
